import { Container } from '@almbrand/container';
import { Grid } from '@almbrand/grid';
import { useDeviceQuery } from '@almbrand/usedevicequery';
import classNames from 'classnames';
import { Component } from 'components/Component';
import { PageContext } from 'context/PageContext';
import { useTilesTracking } from 'hooks/useTilesContainerTracking';
import { useContext } from 'react';
import { TilesContainer } from '../TilesContainer';
import './ContainerLayout.scss';
import { GridCellComponent } from './GridCellComponent';

export const ContainerLayout: React.FC<{ component: ContentApiModel }> = ({ component }) => {
	const pageContext = useContext(PageContext);
	const theme = pageContext.theme;
	const { isMobile } = useDeviceQuery();

	const { style, content } = component ?? {};

	const { innerBackgroundColor, gridStyle, marginBottom, marginTop } = style ?? {};
	const { useReadline, readlineId, contentId, contentType, isInvertedOnMobile, bmetricId } = content ?? {};

	// Tracking
	const cleanTheme = theme?.replace('-theme', '');
	const brandName = cleanTheme as Theme;
	useTilesTracking(component, 'TilesContainerBlock', brandName);

	if (contentType == 'ReadlineBlock') return <Component {...content} />;

	const getDefaultMargin = (value) => {
		return ['', 'default'].includes(value) ? 'spacing-r' : value;
	};

	// Container or block content type
	const isContainer = contentType === 'DefaultContainerBlock' || contentType === 'TilesContainerBlock';

	// Determine container value based on content type and device
	let containerValue: boolean;
	if (isMobile && contentType === 'TilesContainerBlock') {
		containerValue = true;
	} else if (contentType === 'TilesContainerBlock') {
		containerValue = false;
	} else {
		containerValue = gridStyle === 'fullscreen';
	}

	const mapGridCell = (Component: React.ComponentType<{ component: ContentApiModel; applySpacings?: boolean }>) => {
		return content?.mainArea?.items?.map((subItem, subIndex) => {
			return <Component component={subItem} key={subIndex} applySpacings />;
		});
	};

	return (
		<Container
			desktopWidth="fullWidth"
			tabletWidth="fullWidth"
			mobileWidth="fullWidth"
			backgroundColor={innerBackgroundColor?.cssClass}
			id={useReadline && readlineId ? readlineId : contentId}
			className={classNames(`pb-${getDefaultMargin(marginBottom)}`, `pt-${getDefaultMargin(marginTop)}`)}
		>
			<Container
				desktopWidth="1600"
				tabletWidth="1280"
				mobileWidth="744"
				className={contentType === 'DefaultContainerBlock' && bmetricId}
			>
				<Grid
					container={containerValue}
					horizontalCenter={theme === 'Privatsikring-theme' ? true : false}
					nogutter={isContainer}
				>
					{(() => {
						switch (contentType) {
							case 'TilesContainerBlock':
								return (
									<GridCellComponent component={component} applySpacings={false}>
										<Grid container>
											<TilesContainer>{mapGridCell(GridCellComponent)}</TilesContainer>
										</Grid>
									</GridCellComponent>
								);
							case 'DefaultContainerBlock':
								return (
									<GridCellComponent component={component} applySpacings={false}>
										<Grid
											container
											className={classNames(
												contentType === 'DefaultContainerBlock' &&
													isInvertedOnMobile &&
													'revers-order',
											)}
										>
											{mapGridCell(GridCellComponent)}
										</Grid>
									</GridCellComponent>
								);

							default:
								return <GridCellComponent component={component} applySpacings={false} />;
						}
					})()}
				</Grid>
			</Container>
		</Container>
	);
};
